import { forwardOf } from '../level/connectors/Pose';
import type { TurnBend } from '../level/TurnController';
import { clamp, wrapAngle } from '../utils/math';

/** The body on a turn: world-space deviation from the track path and its heading. */
export interface BendPose {
  offsetX: number;
  offsetZ: number;
  yaw: number;
  /** Body speed relative to the track speed (≈ 0.7–1.4 on the bend) – the run cycle follows it, feet keep their grip. */
  speedFactor: number;
}

/**
 * Where the body runs on a 90° turn (presentation only). Gameplay turns on the spot:
 * at the corner – where the runner's lane meets the same lane of the exit – the path
 * heading jumps by 90°. The body follows a cubic Bézier around that corner instead,
 * from up to `length` meters before it (on the approach) to the exit leg behind it:
 * - the control arms are a third of the bend, so the body runs at track speed where the
 *   bend starts and ends and always faces the way it moves – no sideways sliding, no snap;
 * - turn decided in time: symmetric bend ± `length`, cutting the corner by at most
 *   0.35 × `length`;
 * - decided late: the bend starts at the decision and keeps its full length of 2 × `length`
 *   (as gentle as in time), swinging out past the corner before it joins the exit leg;
 * - turned on the spot (decided past the last exit lane, or rescued): `length` after the
 *   corner only – a quick swing that stays clear of the closed far side of the junction.
 * Returns null outside the bend (the body is on the track path).
 */
export function bendPose(bend: TurnBend, length: number, out: BendPose = { offsetX: 0, offsetZ: 0, yaw: 0, speedFactor: 1 }): BendPose | null {
  const before = clamp(bend.leadIn, 0, length);
  const after = before > 0 ? 2 * length - before : length;
  const d = bend.progress;
  if (length <= 0 || d < -before || d > after) return null;

  // Corner coordinates: u along the approach, v along the exit leg, origin in the corner.
  // Control points: P0 = (−before, 0), P1 = P0 + arm·u, P2 = P3 − arm·v, P3 = (0, after).
  const span = before + after;
  const arm = span / 3;
  const p1u = arm - before;
  const p2v = after - arm;
  const t = (d + before) / span;
  const s = 1 - t;
  const bu = s * s * s * -before + 3 * s * s * t * p1u;
  const bv = 3 * s * t * t * p2v + t * t * t * after;
  const tangentU = 3 * s * s * arm - 6 * s * t * p1u;
  const tangentV = 6 * s * t * p2v + 3 * t * t * arm;

  // Track path: straight to the corner, then along the exit leg.
  const offsetU = bu - Math.min(d, 0);
  const offsetV = bv - Math.max(d, 0);
  const u = forwardOf(bend.approachYaw);
  const v = forwardOf(bend.approachYaw + (bend.side * Math.PI) / 2);
  out.offsetX = offsetU * u.x + offsetV * v.x;
  out.offsetZ = offsetU * u.z + offsetV * v.z;
  out.yaw = wrapAngle(bend.approachYaw + bend.side * Math.atan2(tangentV, tangentU));
  out.speedFactor = Math.hypot(tangentU, tangentV) / span;
  return out;
}
